import React, { useState, useEffect } from 'react';
import { Card, Form, InputNumber, Button, Space, message, Descriptions, Spin, Typography } from 'antd';
import { SaveOutlined, SettingOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const adminApi = axios.create({ baseURL: '/api', timeout: 15000 });
adminApi.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
adminApi.interceptors.response.use(r => r, error => {
  if (error.response?.status === 401) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    window.location.href = '/admin/login';
  }
  return Promise.reject(error);
});

const WithdrawalSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState({ minAmount: null, fee: null });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get('/config/settings');
      const all = res.data?.data || {};
      const withdrawal = all['withdrawal'] || [];
      const map = {};
      withdrawal.forEach(s => { map[s.setting_key] = parseFloat(s.setting_value); });
      const vals = {
        minAmount: map['withdrawal_min_amount'] ?? 100,
        fee: map['withdrawal_fee'] ?? 2500
      };
      setCurrent(vals);
      form.setFieldsValue(vals);
    } catch {
      message.error('Gagal memuat pengaturan');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      await Promise.all([
        adminApi.put('/config/settings/withdrawal_min_amount', {
          setting_value: String(values.minAmount),
          setting_type: 'number'
        }),
        adminApi.put('/config/settings/withdrawal_fee', {
          setting_value: String(values.fee),
          setting_type: 'number'
        })
      ]);
      message.success('Pengaturan withdrawal berhasil disimpan');
      fetchSettings();
    } catch {
      message.error('Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;

  return (
    <div style={{ padding: 24, maxWidth: 600 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3}><SettingOutlined /> Pengaturan Withdrawal</Title>
        <Text type="secondary">Konfigurasi biaya dan minimum jumlah withdrawal merchant</Text>
      </div>

      <Card title="Nilai Saat Ini" style={{ marginBottom: 24 }}>
        <Descriptions column={1}>
          <Descriptions.Item label="Minimum Withdrawal">
            <Text strong>Rp {(current.minAmount ?? 0).toLocaleString('id-ID')}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Biaya Admin Withdrawal">
            <Text strong>Rp {(current.fee ?? 0).toLocaleString('id-ID')}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Ubah Pengaturan">
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="minAmount"
            label="Minimum Withdrawal (Rp)"
            rules={[{ required: true, message: 'Wajib diisi' }, { type: 'number', min: 0 }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              size="large"
              formatter={v => `Rp ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={v => v.replace(/Rp\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="fee"
            label="Biaya Admin Withdrawal (Rp)"
            rules={[{ required: true, message: 'Wajib diisi' }, { type: 'number', min: 0 }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              size="large"
              formatter={v => `Rp ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={v => v.replace(/Rp\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />} size="large">
              Simpan Perubahan
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default WithdrawalSettings;
